import { usePageProps, withLayouts } from 'react-dx'
import { useLingui } from '@lingui/react/macro'

import RootLayout from '../_layout/layouts/RootLayout'
import AntdLayout from '../_layout/layouts/AntdLayout'
import SiteSpecificContextLayout from '../_layout/layouts/SiteSpecificContextLayout'
import { paddingHeaderHeightCls } from '../_layout/Header/constants'
import LayoutsDefaultLayout from '../_layout'
import MusicList from '../_components/MusicList'
import { PageStoreProvider } from '../_shared/formStore'
import {
  RAP_FORCED_DEFAULTS,
  createMusicFormStore,
} from '../ai-music-generator/_store/formStore'
import { fluentFlexItemClassName } from '../ai-music-generator/classes'
import GeneratorForm from '../ai-music-generator/_blocks/GeneratorForm'
import { createCoverStore } from '../ai-cover-generator/_store'
import CoverForm from '../ai-cover-generator/_blocks/CoverForm'
import { createTTSStore } from '../text-to-speech/_store'
import TTSForm from '../text-to-speech/_blocks/TTSForm'
import { useJoinCreditsHintVisibility } from '../ai-music-generator/_blocks/GeneratorForm/components/GeneratorLyricsSmart/_hooks/useJoinCreditsHintVisibility'

import { GENERATOR_TYPES, TYPE_TO_URL, getFallbackTitle } from './constants'

import type { GeneratorType } from './constants'
import type { MusicListActionRef } from '../_components/MusicList'
import type { InferGetServerSidePropsType } from 'next'
import type { TNextPagePro } from '@/types/global'

import { NotFoundError } from '@/helpers/errors'
import { getServerSideData } from '@/helpers/server-data'
import { FluentFlex, FluentFlexRoot } from '@/components/FluentFlex'
import { getTadFeatureData } from '@/pages/tad.ai/_services/feature-service'
import { FeatureSeoPage } from '@/pages/tad.ai/_blocks/FeatureSeoPage'
import { RAP_DRAFT_STORAGE_KEY } from '@/pages/tad.ai/_shared/formDraft/draftKeys'
import { useDataTrackViewEventProps } from '@/components/TrackEventManager/hooks/track-view'
import { useAppUIModal } from '@/contexts/AppUIModalContext'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { useIsMounted } from '@/hooks/isMounted'


import { useRef, useCallback, useMemo } from 'react';
import { useGlobalAudioPlayer } from '@/hooks/audio/store';
import { cls } from 'tagged-classnames-free';
import { useConfig } from '@/contexts/SiteContext/hooks';
const createRapFormStore = () =>
  createMusicFormStore({
    persistKey: RAP_DRAFT_STORAGE_KEY,
    forcedFormDefaults: RAP_FORCED_DEFAULTS,
  })

function GeneratorFormSwitch({
  type,
  onCreated,
}: {
  type: GeneratorType
  onCreated: () => void
}) {
  switch (type) {
    case 'music':
      return (
        <PageStoreProvider createStore={createMusicFormStore}>
          <GeneratorForm onCreated={onCreated} />
        </PageStoreProvider>
      )
    case 'rap':
      return (
        <PageStoreProvider createStore={createRapFormStore}>
          <GeneratorForm onCreated={onCreated} />
        </PageStoreProvider>
      )
    case 'cover':
      return (
        <PageStoreProvider createStore={createCoverStore}>
          <CoverForm onCreated={onCreated} />
        </PageStoreProvider>
      )
    case 'tts':
      return (
        <PageStoreProvider createStore={createTTSStore}>
          <TTSForm onCreated={onCreated} />
        </PageStoreProvider>
      )
  }
}

export const Page: TNextPagePro<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const {
    type,
    seoPageData: { hero },
  } = props
  const { t } = useLingui()
  const musicListRef = useRef<MusicListActionRef>(null)
  const { collapsed } = useGlobalAudioPlayer()
  const viewTrackRef = useRef<HTMLDivElement>(null)

  const onCreated = useCallback(() => {
    musicListRef.current?.refetchIfPage1(true)
  }, [])

  useDataTrackViewEventProps(viewTrackRef, {
    name: 'view',
    location: TYPE_TO_URL[type],
  })

  const { signUp } = useAppUIModal()
  const { isSignIn } = useSessionInfo()
  const { isMounted } = useIsMounted()
  const { hasSignedInBefore } = useJoinCreditsHintVisibility(isSignIn)

  const showJoinCreditsHint = useMemo(() => {
    return isMounted && !isSignIn && !hasSignedInBefore
  }, [isMounted, isSignIn, hasSignedInBefore])

  return (
    <FluentFlexRoot itemClassName={fluentFlexItemClassName}>
      <FluentFlex
        className={`min-h-screen flex-col xl:h-screen ${paddingHeaderHeightCls}`}
      >
        <div className='flex w-full items-center justify-between px-8 xl:w-[680px]'>
          <h1
            ref={viewTrackRef}
            id='top'
            className='mb-3 text-xl font-bold md:mb-0 md:text-3xl md:leading-[64px]'
          >
            {hero?.h1 || getFallbackTitle(type, t)}
          </h1>
          {(type === 'tts' || type === 'cover') && showJoinCreditsHint && (
            <div className=' hidden justify-end pb-2 md:flex'>
              <button
                type='button'
                className='before:i-cus--tad-member-gold after:i-com--right-arrow inline-flex size-fit items-center gap-1 rounded-[20px] bg-[#ffcc0029] px-3 py-1.5 text-center text-[rgb(255,235,184)] before:inline-block before:size-4 after:inline-block after:size-4 hover:bg-[#ffcc004d]'
                onClick={() => {
                  signUp.show({ defaultShowSignUp: true })
                }}
              >
                {t`Join & Get 25 Free Credits`}
              </button>
            </div>
          )}
        </div>
        <FluentFlex
          className={cls`block flex-col xl:flex-row ${!collapsed && 'pb-32 xl:pb-24'}`}
        >
          <GeneratorFormSwitch key={type} type={type} onCreated={onCreated} />
          <MusicList
            ref={musicListRef}
            className={cls`mx-auto h-full md:!px-8 xl:max-w-none`}
          />
        </FluentFlex>
      </FluentFlex>
    </FluentFlexRoot>
  )
}

Page.noPageKey = true

const LayoutsPage = withLayouts(
  Page,
  [
    (props) => {
      const { seoPageData } =
        usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()

      return (
        <LayoutsDefaultLayout
          {...props}
          main={{
            className: cls`bg-f-other-1`,
            includingHeader: true,
          }}
          sideBar={{
            className: '',
          }}
        >
          {seoPageData.showFunctionArea ? props.children : null}
          <FeatureSeoPage pageData={seoPageData} />
        </LayoutsDefaultLayout>
      )
    },
    (props) => {
      const { seoPageData } =
        usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()
      const { seoTitle } = useConfig()

      return (
        <RootLayout
          seo={{
            title: seoTitle(seoPageData.title),
            description: seoPageData.description,
          }}
          {...props}
        />
      )
    },
    (props) => {
      return <SiteSpecificContextLayout {...props} />
    },
    AntdLayout,
  ],
  {
    propertiesHoist: ['noPageKey'],
  },
)

export default LayoutsPage

// eslint-disable-next-line react-refresh/only-export-components
export const getServerSideProps = getServerSideData({
  cacheControl: true,
})(async (hostInfo, ctx, helpers) => {
  const type = ctx?.params?.type as string

  if (!GENERATOR_TYPES.includes(type as GeneratorType)) {
    throw new NotFoundError(`Invalid generator type: ${type}`)
  }

  const trpcHelpers = await helpers.getTrpcHelpers()
  const locale = ctx?.locale || 'en'
  const originalUrl = TYPE_TO_URL[type as GeneratorType]

  const seoPageDataPromise = getTadFeatureData(originalUrl, locale)

  const [seoPageData] = await Promise.all([
    seoPageDataPromise,
    trpcHelpers.preset.labelsTree
      .prefetch({ language: locale })
      .catch(() => {}),
    trpcHelpers.tts.getVoicePreviewAudio
      .prefetch({ language: locale })
      .catch(() => {}),
  ])

  return {
    type: type as GeneratorType,
    seoPageData,
    trpcState: trpcHelpers.dehydrate(),
  }
})
